<template>
    <div>
        <DCard
            padding
        >
            <DCol>
                <DRow
                    class="mb-3"
                    align-horizontal="start"
                    align-vertical="end"
                >
                    <DCol
                        sm="3"
                        class="pt-1"
                    >
                        <DInput
                            id="search"
                            v-model="filterParams.search"
                            :label="__('messages.search')"
                            class="mb-0"
                            @keydown.enter.exact.prevent="changePage(1)"
                        />
                    </DCol>
                    <DCol
                        sm="3"
                        class="d-flex gap-2"
                    >
                        <DButton
                            id="filter-apply"
                            :label="__('messages.actions.filter')"
                            @click="changePage(1)"
                        />
                        <DButton
                            id="filter-reset"
                            icon="filter-off"
                            :label="__('messages.actions.reset_filter')"
                            @click="resetFilters"
                        />
                    </DCol>
                    <DCol
                        sm="6"
                        class="text-right"
                    >
                        <DButton
                            id="filter-advanced"
                            icon="magnify"
                            :label="__('messages.actions.advanced_filter')"
                            @click="$inertia.get($route('customers.customers.advanced-search.index'))"
                        />
                        <DButton
                            id="table-configuration"
                            icon="cog-outline"
                            :label="__('messages.list_configuration')"
                            @click="showListViewModal = true"
                        />
                    </DCol>
                </DRow>
            </DCol>
            <DTable
                :fields="getFields"
                :items="customers.data"
                :sorter="isSortable"
                :sorter-value="sorterValue"
                class="table-responsive"
                @update:sorter-value="updateOrdering"
            >
                <template #firstname="{item}">
                    <td>
                        {{ removeEmpty(item.firstname) }}
                    </td>
                </template>
                <template #lastname="{item}">
                    <td>
                        {{ removeEmpty(item.lastname) }}
                    </td>
                </template>
                <template #company="{item}">
                    <td>
                        {{ removeEmpty(item.company) }}
                    </td>
                </template>
                <template #email="{item}">
                    <td>
                        {{ removeEmpty(item.email) }}
                    </td>
                </template>
                <template #id="{item}">
                    <td>
                        <Link :href="$route('customers.customers.show', {customer: item.id})">
                            {{ item.id }}
                        </Link>
                    </td>
                </template>
                <template #brand="{item}">
                    <td>
                        {{ item.brand }}
                    </td>
                </template>
                <template #status="{item}">
                    <td>
                        <DTag
                            :color="item.status === 'active' ? 'success' : 'danger'"
                            :label="__('customers.customers.' + item.status)"
                        />
                    </td>
                </template>
                <template #address="{item}">
                    <td>
                        {{ item.address1 }}<br>
                        {{ item.address2 }}<br v-if="item.address2">
                        {{ item.postal_code }} {{ item.city }}<br>
                        {{ item.country ? item.country_name : '' }}
                    </td>
                </template>
                <template #business_id_ssn="{item}">
                    <td>
                        {{ showBusinessIdSsn(item) }}
                    </td>
                </template>
                <template #incident="{item}">
                    <td>
                        <IncidentFlag
                            v-if="userPermissions.includes('customer.incidents_view') && item.has_incidents"
                            id="orders-incident-flag"
                            :active-incident="item.has_open_incidents"
                            :no-link="true"
                        />
                    </td>
                </template>
                <template #market_region="{item}">
                    <td>
                        {{ item.market_region.name }}
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <div style="display: flex; grid-gap: 10px;">
                            <DButton
                                icon="eye"
                                mini
                                @click="$inertia.get($route('customers.customers.show', {customer: item.id}))"
                            />
                            <DButton
                                v-if="userPermissions.includes('customer.edit')"
                                icon="pencil"
                                mini
                                @click="$inertia.get(editCustomerLink(item.id))"
                            />
                        </div>
                    </td>
                </template>
            </DTable>
            <ListViewModal
                v-if="showListViewModal"
                :show-modal="showListViewModal"
                :user-list-view="userListView"
                @close-modal="showListViewModal = false"
            />
        </DCard>
        <DPagination
            v-if="customers.last_page > 1"
            class="pb-2"
            :active-page="customers.current_page"
            :pages="customers.last_page"
            align="center"
            @update:activePage="changePage"
        />

        <DFabs>
            <DFab
                v-if="userPermissions.includes('customer.create')"
                id="btn-add"
                icon="plus"
                :label="__('customers.customers.add_customer')"
                @click="$inertia.get($route('customers.customers.create'))"
            />
        </DFabs>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'

export default {
    name: 'CustomerIndex',
    components: {
        ListViewModal,
        Link,
        IncidentFlag
    },
    props: {
        customers: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default: () => {
                return {}
            }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    data () {
        return {
            filterParams: {
                search: this.filters.search,
                order_by: this.filters.order_by,
                order_by_asc: this.filters.order_by_asc ? 1 : 0,
                save_filters: false
            },
            showListViewModal: false
        }
    },
    computed: {
        sorterValue () {
            return {
                column: this.filterParams.order_by,
                asc: !!this.filters.order_by_asc
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'actions' ? this.__('settings.list_view.' + item.list + '.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            return availableList
        }
    },
    methods: {
        editCustomerLink (id) {
            return this.$route('customers.customers.edit', id)
        },
        removeEmpty (value) {
            return value || ''
        },
        showBusinessIdSsn (customer) {
            return [customer.business_id, customer.ssn].filter((v) => v !== null).join(' / ')
        },
        changePage (page) {
            this.filterParams.save_filters = true
            const queryParameters = Object.fromEntries(Object.entries(this.filterParams).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters._query = { page: page, save_filters: false }
            }

            this.$inertia.get(this.$route('customers.customers.index', queryParameters))
        },
        updateOrdering (event) {
            this.filterParams.order_by = event.column
            this.filterParams.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.customers.current_page)
        },
        resetFilters () {
            this.$inertia.get(this.$route('customers.customers.index', {
                page: this.customers.current_page,
                save_filters: true,
                default_filters: true
            }))
        }
    }
}
</script>
